import { useLingui } from '@lingui/react';
import {
  Button,
  Checkbox,
  Collapse,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CocoModal from 'components/coco-modal/coco-modal';
import EmailIcon from '@mui/icons-material/Email';
import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { msg, Trans } from '@lingui/macro';
import MutliEmailInput, {
  sendInvites,
} from 'components/multi-email-input/MultiEmailInput';
import { LoadingButton } from '@mui/lab';
import { useCommunityDetails } from 'atoms/community-details';
import ConnectGoogleClassroom from 'components/social-login-buttons/ConnectGoogleClassroom';
import { useUserProfile } from 'atoms/user-profile';
import { httpClient } from '@cocoplatform/coco-rtc-client';
import {
  ChevronLeft,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  Info,
  Search,
} from '@mui/icons-material';
import PrimaryLoader from 'components/primary-loader/primary-loader';
import LoaderMessage from 'components/loader/LoaderMessage';
import GoogleIcon from '@mui/icons-material/Google';
import { Box } from '@mui/system';
import { useFlags } from 'utils/feature-flags';

import ClassroomIcon from './google-classroom.png';
import { ShareLinkPage } from 'components/add-members-modal/AddMembersLoginModal';
import ChevronRight from '@mui/icons-material/ChevronRight';
import TagList from 'components/tag-list/TagList';

import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import Swal from 'sweetalert2';
import ControlledTextField from 'components/forms/ControlledTextField';
import { useForm, useWatch } from 'react-hook-form';

export interface Classroom {
  id: string;
  name: string;
  section: string;
  ownerId: string;
}

export interface Student {
  id: string;
  name: string;
  email: string;
  role?: string;
  type: 'STUDENT' | 'TEACHER';
}

export interface Teacher {
  id: string;
  name: string;
  email: string;
  role?: string;
  type: 'TEACHER' | 'STUDENT';
}

async function sendClassroomEmails(
  invitationcodeIDs: string[],
  communityId: string,
) {
  const res = await httpClient.post(
    `/community/${communityId}/classroom-invitations`,
    {
      invitationIDs: invitationcodeIDs,
    },
  );

  if (res.status !== 200) {
    throw new Error('Failed to send invites');
  }

  const { inviteCount } = res.data;

  return inviteCount;
}

async function sendClassroomInvites(
  classrooms: {
    id: string;
  }[],
  emails: {
    email: string;
    role: string;
    classroomId?: string;
    type: 'STUDENT' | 'TEACHER';
  }[],
  classroomMetadata: {
    [classroomId: string]: {
      id: string;
      name: string;
      section: string;
    };
  },
  communityId: string,
) {
  const res = await httpClient.put(
    `/community/${communityId}/classroom-membership`,
    {
      classrooms,
      emails,
      classroomMetadata,
    },
  );

  if (res.status !== 200) {
    throw new Error('Failed to send invites');
  }

  const { invitationcodeIDs } = res.data;
  return invitationcodeIDs;
}

async function sendEmails(invitationcodeIDs: string[], communityId: string) {
  const res = await httpClient.post(
    `/community/${communityId}/classroom-invitations`,
    {
      invitationcodeIDs,
    },
  );

  if (res.status !== 200) {
    throw new Error('Failed to send invites');
  }
}

function ClassroomView(p: {
  classroom: Classroom;
  setSelectedUsers: (users: SetStateAction<SelectedUser[]>) => void;
  selectedUsers: SelectedUser[];
  setClassroom: (classroom: string | null) => void;

  selectedClassrooms: Set<string>;
  toggleClassroomSelection: (classroom: Classroom) => void;

  communityId: string;
}) {
  const [view, setView] = useState<'students' | 'teachers'>('students');

  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  const [students, setStudents] = useState<Student[] | null>(null);
  const [teachers, setTeachers] = useState<Teacher[] | null>(null);

  const { community } = useCommunityDetails({
    communityId: p.communityId,
    autoLoad: false,
  });

  const classroomAdded =
    (community?.googleClassrooms || []).findIndex(
      (c) => c.id === p.classroom.id,
    ) !== -1;

  const getClassroomStudents = useCallback(
    async (pageToken?: string) => {
      setLoadingStudents(true);
      const queryParams = new URLSearchParams();
      if (pageToken) {
        queryParams.append('pageToken', pageToken);
      }
      const res = await httpClient.get(
        `/user/google-classroom/classrooms/${p.classroom.id}/students?${queryParams.toString()}`,
      );
      setLoadingStudents(false);
      if (res.status === 200) {
        let { students, nextPageToken } = res.data;

        students = students.map((student) => {
          return {
            ...student,
            type: 'STUDENT',
          };
        });
        setStudents((s) => {
          return s ? [...s, ...students] : students;
        });

        if (nextPageToken) {
          getClassroomStudents(nextPageToken);
        }
      }
    },
    [p.classroom.id],
  );

  const getClassroomTeachers = useCallback(
    async (pageToken?: string) => {
      setLoadingTeachers(true);
      const queryParams = new URLSearchParams();
      if (pageToken) {
        queryParams.append('pageToken', pageToken);
      }
      const res = await httpClient.get(
        `/user/google-classroom/classrooms/${p.classroom.id}/teachers?${queryParams.toString()}`,
      );
      setLoadingTeachers(false);
      if (res.status === 200) {
        let { teachers, nextPageToken } = res.data;

        teachers = teachers.map((teacher) => {
          return {
            ...teacher,
            type: 'TEACHER',
          };
        });

        setTeachers((t) => {
          return t ? [...t, ...teachers] : teachers;
        });

        if (nextPageToken) {
          getClassroomTeachers(nextPageToken);
        }
      }
    },
    [p.classroom.id],
  );

  useEffect(() => {
    getClassroomStudents();
    getClassroomTeachers();
  }, []);

  const { control } = useForm();

  const search = useWatch({
    name: 'search',
    control,
  });

  const { _ } = useLingui();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { userProfile } = useUserProfile();

  const isLoading = students === null || teachers === null;

  if (isLoading) {
    return (
      <Stack alignItems='center'>
        <LoaderMessage message='Loading...' mode='subtle' />
      </Stack>
    );
  }

  const teachersExceptMe = teachers.filter(
    (t) => t.email !== userProfile?.email,
  );

  const activeList = view === 'students' ? students : teachersExceptMe;

  const studentSelections = students?.filter((student) =>
    p.selectedUsers.find(
      (u) => u.classroomId === p.classroom.id && u.email === student.email,
    ),
  );

  const teacherSelections = teachers?.filter((teacher) =>
    p.selectedUsers.find(
      (u) => u.classroomId === p.classroom.id && u.email === teacher.email,
    ),
  );

  const totalSelections =
    (studentSelections?.length || 0) + (teacherSelections?.length || 0);

  const allSelected = p.selectedClassrooms.has(p.classroom.id);

  const allSelectedInCurrentView =
    view === 'students'
      ? studentSelections?.length === students?.length
      : teacherSelections?.length === teachers?.length;

  const filteredList = activeList?.filter(
    (user) =>
      !search ||
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase()),
  );

  if (students.length === 0 && teachersExceptMe.length === 0) {
    return (
      <Stack alignItems='center'>
        <Stack direction='row' alignItems='center' gap='0.5rem'>
          <ErrorOutline />
          <Typography>
            <Trans>
              No users found in this class. Please add users to your Google
              Classroom before inviting them to CoCo.
            </Trans>
          </Typography>
        </Stack>
        <Box mt={2} />
        <Button
          onClick={() => {
            p.setClassroom(null);
          }}
          variant='contained'
          color='primary'
        >
          <Trans>Go Back</Trans>
        </Button>
      </Stack>
    );
  }

  return (
    <Stack>
      <Typography variant='body2' color='var(--color-subtext)' mb={2}>
        <Trans>
          Select all or specific users within this class to invite to this
          community:
        </Trans>
      </Typography>
      <ControlledTextField
        name='search'
        variant='outlined'
        placeholder={_(msg`Search users within this class…`)}
        autoComplete='new-username'
        size='small'
        control={control}
        InputProps={{
          startAdornment: (
            <Search
              sx={{
                color: 'var(--color-subtext)',
                marginRight: '0.5rem',
              }}
            />
          ),
        }}
      />
      <TableContainer
        sx={{
          maxHeight: '250px',
          overflow: 'scroll',
        }}
      >
        <Table
          sx={{
            '& .MuiTableCell-root': {
              padding: '0.5rem',
            },
            '& .MuiTableRow-root:last-child th, & .MuiTableRow-root:last-child td':
            {
              borderBottom: 0,
            },
          }}
        >
          <TableHead
            sx={{
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
            }}
          >
            <TableRow>
              <TableCell width='50%'>
                <TagList
                  tags={[
                    {
                      label: _(msg`Students`),
                      id: 'students',
                      icon: <PersonIcon />,
                    },
                    {
                      label: _(msg`Teachers`),
                      id: 'teachers',
                      icon: <SchoolIcon />,
                    },
                  ]}
                  onSelect={(tagId) => {
                    setView(tagId as 'students' | 'teachers');
                  }}
                  selectedId={view}
                />
              </TableCell>
              {view === 'teachers' && teachersExceptMe?.length > 0 && (
                <TableCell>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='center'
                    gap={1}
                    sx={{
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      setAnchorEl(e.currentTarget);
                    }}
                    onMouseLeave={() => {
                      setAnchorEl(null);
                    }}
                  >
                    <Typography variant='body2' fontWeight='600'>
                      <Trans>Invite As</Trans>
                    </Typography>
                    <Info
                      sx={{
                        fill: 'var(--color-primary)',
                        width: '1.2rem',
                      }}
                    />
                  </Stack>
                  <Popover
                    id={'role-info'}
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => {
                      setAnchorEl(null);
                    }}
                    sx={{
                      pointerEvents: 'none',
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                  >
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <Typography fontWeight={600}>
                                <Trans>Permissions</Trans>
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={600}>
                                <Trans>Member</Trans>
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={600}>
                                <Trans>Manager</Trans>
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={600}>
                                <Trans>Admin</Trans>
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              <Trans>
                                Can create and explore CoCo spaces in this
                                community.
                              </Trans>
                            </TableCell>
                            <TableCell>✅</TableCell>
                            <TableCell>✅</TableCell>
                            <TableCell>✅</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Trans>
                                Can delete spaces, add / remove members, and
                                update media library / community settings.
                              </Trans>
                            </TableCell>
                            <TableCell>❌</TableCell>
                            <TableCell>✅</TableCell>
                            <TableCell>✅</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Trans>
                                Can edit community details and delete this
                                community.
                              </Trans>
                            </TableCell>
                            <TableCell>❌</TableCell>
                            <TableCell>❌</TableCell>
                            <TableCell>✅</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Popover>
                </TableCell>
              )}
              {(activeList?.length || 0) > 0 && (
                <TableCell>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='flex-end'
                    pr={1}
                  >
                    <Typography variant='body2' fontWeight='600'>
                      <Trans>Select</Trans>
                    </Typography>
                    <Checkbox
                      checked={allSelected || allSelectedInCurrentView}
                      onClick={(e) => {
                        if (allSelected) {
                          p.toggleClassroomSelection(p.classroom);

                          // add all other users except the one in current view
                          p.setSelectedUsers((users) => {
                            const newUsers = users.concat(
                              students
                                .concat(teachersExceptMe)
                                .filter(
                                  (s) =>
                                    !activeList?.find(
                                      (u) => u.email === s.email,
                                    ),
                                )
                                .map((u) => {
                                  return {
                                    classroomId: p.classroom.id,
                                    email: u.email,
                                    role: u.role || 'MEMBER',
                                    type: u.type,
                                  };
                                }),
                            );

                            return newUsers;
                          });

                          return;
                        }

                        if (allSelectedInCurrentView) {
                          // remove all users in current view
                          p.setSelectedUsers((users) => {
                            const newUsers = users.filter(
                              (u) =>
                                !activeList?.find(
                                  (user) => user.email === u.email,
                                ),
                            );
                            return newUsers;
                          });

                          return;
                        }

                        // add users in current view
                        p.setSelectedUsers((users) => {
                          const newUsers = users.concat(
                            activeList?.map((u) => {
                              return {
                                classroomId: p.classroom.id,
                                email: u.email,
                                role: u.role || 'MEMBER',
                                type: u.type,
                              };
                            }) || [],
                          );

                          return newUsers;
                        });
                      }}
                    />
                  </Stack>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {activeList?.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Stack
                    mt={2}
                    direction='row'
                    alignItems='center'
                    gap='0.5rem'
                  >
                    <ErrorOutline />

                    <Typography variant='body2'>
                      <Trans>
                        No {view === 'students' ? 'students' : 'teachers'} found
                        in this class. Please add them to your Google Classroom
                        before inviting them to CoCo.
                      </Trans>
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {activeList?.length !== 0 && filteredList?.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Stack
                    mt={2}
                    direction='row'
                    alignItems='center'
                    gap='0.5rem'
                  >
                    <ErrorOutline />

                    <Typography variant='body2'>
                      <Trans>No users found matching your search.</Trans>
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {filteredList?.map((user) => {
              const userSelected = p.selectedUsers.find(
                (u) =>
                  u.classroomId === p.classroom.id && u.email === user.email,
              );

              const memberAdded =
                classroomAdded ||
                (community?.members || []).findIndex(
                  (m) => m.user?.email === user.email || m.email === user.email,
                ) !== -1;

              return (
                <TableRow
                  key={user.id}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor:
                      userSelected || allSelected || memberAdded
                        ? '#F9F7FF'
                        : 'transparent',
                  }}
                >
                  <TableCell>
                    <Stack>
                      <Typography variant='body2'>{user.name}</Typography>
                      <Typography variant='caption'>{user.email}</Typography>
                    </Stack>
                  </TableCell>
                  {view === 'teachers' && (
                    <TableCell>
                      <Select
                        variant='standard'
                        value={user.role || 'MEMBER'}
                        onChange={(e) => {
                          setTeachers((teachers) => {
                            return (
                              teachers?.map((t) => {
                                if (t.email === user.email) {
                                  return {
                                    ...t,
                                    role: e.target.value,
                                  };
                                }
                                return t;
                              }) || null
                            );
                          });
                          p.setSelectedUsers((users) => {
                            return users.map((u) => {
                              if (
                                u.classroomId === p.classroom.id &&
                                u.email === user.email
                              ) {
                                return {
                                  ...u,
                                  role: e.target.value,
                                };
                              }
                              return u;
                            });
                          });
                        }}
                        fullWidth
                        disableUnderline
                        sx={{
                          '& .MuiSelect-root': {
                            padding: '0.5rem',
                          },
                          textAlign: 'center',
                        }}
                      >
                        <MenuItem value='MEMBER'>
                          <Typography variant='body2'>
                            <Trans>Member</Trans>
                          </Typography>
                        </MenuItem>
                        <MenuItem value='MANAGER'>
                          <Typography variant='body2'>
                            <Trans>Manager</Trans>
                          </Typography>
                        </MenuItem>
                        <MenuItem value='OWNER'>
                          <Typography variant='body2'>
                            <Trans>Admin</Trans>
                          </Typography>
                        </MenuItem>
                      </Select>
                    </TableCell>
                  )}
                  <TableCell align='right'>
                    <Stack pr={1} alignItems='end'>
                      <Checkbox
                        disabled={memberAdded}
                        checked={!!userSelected || allSelected || memberAdded}
                        onChange={(e) => {
                          if (allSelected && !e.target.checked) {
                            p.toggleClassroomSelection(p.classroom);

                            // add all other users in selected users except this user
                            p.setSelectedUsers((users) => {
                              const newUsers = students
                                .concat(teachersExceptMe)
                                .filter((s) => s.email !== user.email)
                                .map((u) => {
                                  return {
                                    classroomId: p.classroom.id,
                                    email: u.email,
                                    role: u.role || 'MEMBER',
                                    type: u.type,
                                  };
                                })
                                .concat(users);

                              return newUsers;
                            });

                            return;
                          }

                          if (e.target.checked) {
                            p.setSelectedUsers((users) => {
                              return users.concat([
                                {
                                  classroomId: p.classroom.id,
                                  email: user.email,
                                  role: user.role || 'MEMBER',
                                  type: user.type,
                                },
                              ]);
                            });
                          } else {
                            p.setSelectedUsers((users) => {
                              return users.filter(
                                (u) =>
                                  u.classroomId !== p.classroom.id ||
                                  u.email !== user.email,
                              );
                            });
                          }
                        }}
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack justifyContent='center' alignItems='center' mt={2}>
        {(loadingStudents || loadingTeachers) && (
          <LoaderMessage message={_(msg`Loading...`)} mode='subtle' />
        )}
      </Stack>
      <Box mt={2} />
      <Divider />
      <Box mt={2} />
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography variant='caption'>
          {allSelected ? (
            <Trans>All user selected</Trans>
          ) : (
            <Trans>
              {totalSelections} user
              {totalSelections > 1 ? 's' : ''} selected in this class
            </Trans>
          )}
        </Typography>
        <Box />
        <Button
          size='small'
          onClick={() => {
            p.setClassroom(null);
          }}
          variant='contained'
          color='primary'
        >
          Done
        </Button>
      </Stack>
    </Stack>
  );
}

function ClassroomList(p: {
  classrooms: Classroom[];
  setSelectedUsers: (users: SelectedUser[]) => void;
  toggleClassroomSelection: (classroom: Classroom) => void;
  selectedClassrooms: Set<string>;
  selectedUsers: SelectedUser[];
  selectedClassroom: string | null;
  setSelectedClassroom: (classroom: string | null) => void;
  setSelectedClassrooms: (classrooms: Set<string>) => void;
  uniqueClassroomsSelected: Set<string>;
  communityId: string;
}) {
  const { _ } = useLingui();

  const { control } = useForm();

  const search = useWatch({
    name: 'search',
    control,
  });

  const filteredClassrooms = p.classrooms.filter(
    (classroom) =>
      !search ||
      `${classroom.name} ${classroom.section}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );

  const { community } = useCommunityDetails({
    communityId: p.communityId,
    autoLoad: false,
  });

  if (p.classrooms?.length === 0) {
    return (
      <Stack direction='row' alignItems='center' gap='0.5rem'>
        <ErrorOutline />
        <Typography variant='body2'>
          <Trans>
            No classes found linked to your account. Please create or join a
            class in Google Classroom. After that, you can import users to CoCo.
          </Trans>
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack height='100%'>
      <Box>
        <Typography variant='body2' color='var(--color-subtext)'>
          <Trans>
            Select a whole class or click 'View' to select specific users within
            any class:
          </Trans>
        </Typography>
      </Box>
      <Box mt={2} />
      <ControlledTextField
        name='search'
        variant='outlined'
        placeholder={_(msg`Search your classes`)}
        autoComplete='new-username'
        size='small'
        control={control}
        InputProps={{
          startAdornment: (
            <Search
              sx={{
                color: 'var(--color-subtext)',
                marginRight: '0.5rem',
              }}
            />
          ),
        }}
      />

      <TableContainer
        sx={{
          maxHeight: '250px',
          overflowY: 'scroll',
        }}
      >
        <Table
          sx={{
            '& .MuiTableCell-root': {
              padding: '0.5rem',
            },
            '& .MuiTableRow-root:last-child th, & .MuiTableRow-root:last-child td':
            {
              borderBottom: 0,
            },
          }}
        >
          <TableHead
            sx={{
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
            }}
          >
            <TableRow>
              <TableCell width='50%'>
                <Typography variant='body2' fontWeight='600'>
                  <Trans>Class / Section</Trans>
                </Typography>
              </TableCell>
              <TableCell width='30%'>
                <Stack direction='row' alignItems='center'>
                  <Checkbox
                    checked={p.selectedClassrooms.size === p.classrooms.length}
                    onClick={(e) => {
                      if (p.selectedClassrooms.size === p.classrooms.length) {
                        p.setSelectedClassrooms(new Set());
                        p.setSelectedUsers([]);
                      } else {
                        p.setSelectedClassrooms(
                          new Set(p.classrooms.map((c) => c.id)),
                        );
                        p.setSelectedUsers([]);
                      }
                    }}
                  />
                  <Typography variant='body2' fontWeight='600'>
                    <Trans>Select</Trans>
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell width='20%'>
                <Typography variant='body2' fontWeight='600'>
                  <Trans>Actions</Trans>
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {p.classrooms.length !== 0 && filteredClassrooms.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Stack
                    mt={2}
                    direction='row'
                    alignItems='center'
                    gap='0.5rem'
                  >
                    <ErrorOutline />
                    <Typography variant='body2'>
                      <Trans>No classes found matching your search.</Trans>
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {filteredClassrooms.map((classroom) => {
              const selectedClassroom = p.selectedClassrooms.has(classroom.id);
              const selectedUserCount = Array.from(p.selectedUsers).filter(
                (user) => user.classroomId === classroom.id,
              ).length;

              const classroomAdded =
                (community?.googleClassrooms || []).findIndex(
                  (c) => c.id === classroom.id,
                ) !== -1;

              const selectionText = selectedClassroom ? (
                <Trans>All users</Trans>
              ) : selectedUserCount > 0 ? (
                <Trans>
                  {selectedUserCount} user{selectedUserCount > 1 ? 's' : ''}{' '}
                </Trans>
              ) : (
                <></>
              );
              return (
                <>
                  <TableRow
                    key={classroom.id}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor:
                        p.selectedClassrooms.has(classroom.id) ||
                          p.uniqueClassroomsSelected.has(classroom.id) ||
                          classroomAdded
                          ? '#F9F7FF'
                          : 'transparent',
                    }}
                  >
                    <TableCell>
                      <Stack>
                        <Typography variant='body2'>
                          {classroom.name}
                        </Typography>
                        <Typography variant='caption'>
                          {classroom.section}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction='row' alignItems='center'>
                        <Checkbox
                          disabled={classroomAdded}
                          checked={selectedClassroom || classroomAdded}
                          onClick={(e) => {
                            p.toggleClassroomSelection(classroom);
                          }}
                        />
                        <Typography
                          color='var(--color-subtext)'
                          variant='body2'
                        >
                          {selectionText}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction='row' alignItems='center'>
                        <Button
                          size='small'
                          onClick={() => {
                            p.setSelectedClassroom(classroom.id);
                          }}
                          endIcon={<ChevronRight />}
                        >
                          <Trans>View</Trans>
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

interface SelectedUser {
  classroomId: string;
  email: string;
  role: string;
  type: 'STUDENT' | 'TEACHER';
}

export default function ImportGoogleClassroomModal({
  onClose,
  communityId,
  onSubmit,
  onCancel,
}: {
  onClose: () => void;
  onCancel?: () => void;
  communityId: string;
  onSubmit?: () => void;
}) {
  const { _ } = useLingui();

  const { userProfile } = useUserProfile();

  const [connected, setConnected] = useState(false);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);

  const [checkingConnection, setCheckingConnection] = useState(true);
  const [fetchingClassrooms, setFetchingClassrooms] = useState(false);

  const [inviting, setInviting] = useState(false);

  const { loadCommunityDetails, community } = useCommunityDetails({
    communityId,
  });

  const [shareComplete, setShareComplete] = useState(false);

  const classRoomButtonContainer = useRef<HTMLDivElement>(null);

  const [invitationcodeIDs, setInvitationcodeIDs] = useState<string[] | null>(
    null,
  );

  const [invitesSent, setInvitesSent] = useState<null | number>(null);

  const code = community?.genericInvitationCode?.code;
  const displayURL = `${location.origin}/#${code}`.replace(
    'http://localhost',
    'https://coco.build',
  );

  const toggleClassroomSelection = (classroom: Classroom) => {
    if (selectedClassrooms.has(classroom.id)) {
      setSelectedClassrooms((classrooms) => {
        const newClassrooms = new Set(classrooms);
        newClassrooms.delete(classroom.id);
        return newClassrooms;
      });
    } else {
      setSelectedClassrooms((classrooms) => {
        const newClassrooms = new Set(classrooms);
        newClassrooms.add(classroom.id);
        return newClassrooms;
      });
    }

    // unselect all users in the classroom as
    // global selection is toggled
    setSelectedUsers((users) => {
      const newUsers = [
        ...users.filter((user) => user.classroomId !== classroom.id),
      ];
      return newUsers;
    });
  };

  useEffect(() => {
    // Check if the user is already connected to Google Classroom
    async function checkClassRoomConnected() {
      const userId = userProfile?.id;

      setCheckingConnection(true);
      const res = await httpClient.get(`/user/google-classroom`);
      if (res.status === 200 && res.data?.connected) {
        setConnected(true);
      }
      setCheckingConnection(false);
    }
    checkClassRoomConnected();
  }, []);

  useEffect(() => {
    async function getClassRooms() {
      if (connected) {
        setFetchingClassrooms(true);
        // get classrooms and users
        const res = await httpClient.get(`/user/google-classroom/classrooms`);
        if (res.status === 200) {
          setClassrooms(res.data.classrooms);
        }
        setFetchingClassrooms(false);
      }
    }
    if (connected) {
      // get classrooms and users
      getClassRooms();
    }
  }, [connected]);

  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);

  const [selectedClassrooms, setSelectedClassrooms] = useState<Set<string>>(
    new Set(),
  );

  const [classroom, setClassroom] = useState<string | null>(null);

  if (checkingConnection || fetchingClassrooms || !connected) {
    return (
      <CocoModal
        onClose={onClose}
        header={
          <Stack direction='row'>
            <GoogleIcon
              sx={{
                marginRight: '0.5rem',
              }}
            />
            <Typography fontWeight={600} color='white'>
              <Trans>Import users from Google Classroom</Trans>
            </Typography>
          </Stack>
        }
      >
        <Stack minWidth='500px' padding={3}>
          {checkingConnection ? (
            <Typography>
              <Trans>
                Checking if you are connected to Google Classroom...
              </Trans>
            </Typography>
          ) : null}
          {fetchingClassrooms ? (
            <Stack alignItems='center' justifyContent='center' gap={2}>
              <LoaderMessage
                message={_(msg`Fetching your google classrooms...`)}
                mode='subtle'
              />
            </Stack>
          ) : null}
          {!connected && !checkingConnection && !fetchingClassrooms && (
            <Stack>
              <Typography>
                <Trans>
                  <ul>
                    <li>
                      Please connect your Google Classroom account to easily
                      import users.
                    </li>
                    <li>
                      After signing in, make sure to click "Select all" to grant
                      the necessary permissions for CoCo to import your
                      classroom information.
                    </li>
                    <li>
                      <strong>Note:</strong> CoCo does not store any additional
                      Google Classroom data beyond what is needed to import
                      users, such as names and email addresses.
                    </li>
                  </ul>
                </Trans>
              </Typography>
              <Box mt={1} />
              <ConnectGoogleClassroom
                onSuccess={() => {
                  setConnected(true);
                }}
              />
              <Box mt={4} />
              <Stack
                sx={{
                  fontSize: '0.8rem',
                }}
                direction='row'
                justifyContent='space-evenly'
              >
                <Link
                  sx={{
                    color: 'var(--color-subtext)',
                    textDecoration: 'underline !important',
                  }}
                  target='_blank'
                  href='/privacy-terms#:~:text=16.%20Google%20Limited%20Use%20Policy'
                >
                  Limited Use Disclosure
                </Link>
                <Link
                  sx={{
                    color: 'var(--color-subtext)',
                    textDecoration: 'underline !important',
                  }}
                  target='_blank'
                  href='/privacy-terms'
                >
                  Privacy Policy
                </Link>
                <Link
                  sx={{
                    color: 'var(--color-subtext)',
                    textDecoration: 'underline !important',
                  }}
                  target='_blank'
                  href='/terms-of-use'
                >
                  Terms of Use
                </Link>
              </Stack>
            </Stack>
          )}
        </Stack>
      </CocoModal>
    );
  }

  if (shareComplete) {
    return (
      <CocoModal
        onClose={onClose}
        header={
          <Stack direction='row'>
            <EmailIcon style={{ width: '2rem' }} />
            <Typography fontWeight={600} color='white'>
              <Trans>Share Link with Students</Trans>
            </Typography>
          </Stack>
        }
      >
        <ShareLinkPage
          communityId={communityId}
          showGoogleClassroom
          invitesSent={invitesSent}
          sendClassroomInvites={async () => {
            const inviteCount = await sendClassroomEmails(
              invitationcodeIDs!,
              communityId,
            );
            setInvitesSent(inviteCount);
          }}
        />
      </CocoModal>
    );
  }

  if (classroom) {
    const c = classrooms.find((c) => c.id === classroom);
    return (
      <CocoModal
        onClose={onClose}
        header={
          <Stack
            sx={{
              width: '100%',
            }}
            direction='row'
            justifyContent='space-between'
          >
            <ChevronLeft
              sx={{
                width: '2.5rem',
                cursor: 'pointer',
              }}
              onClick={() => {
                setClassroom(null);
              }}
            />
            <Stack direction='row'>
              <Typography
                // sx={{
                //   borderRight: '1px solid rgba(255, 255, 255, 1)',
                // }}
                fontWeight='600'
                color='white'
              >
                {c?.name}
              </Typography>
              {c?.section && (
                <>
                  &nbsp;
                  <Typography color='white'>| {c?.section}</Typography>
                </>
              )}
            </Stack>
            <Box></Box>
          </Stack>
        }
      >
        <Stack
          width='70vw'
          minWidth='350px'
          maxWidth='600px'
          padding={3}
          justifyContent='space-between'
        >
          <ClassroomView
            classroom={classrooms.find((c) => c.id === classroom)!}
            setSelectedUsers={setSelectedUsers}
            selectedUsers={selectedUsers}
            setClassroom={setClassroom}
            selectedClassrooms={selectedClassrooms}
            toggleClassroomSelection={toggleClassroomSelection}
            communityId={communityId}
          />
        </Stack>
      </CocoModal>
    );
  }

  const uniqueClassroomsSelected = new Set(
    Array.from(selectedUsers)
      .map((user) => user.classroomId)
      .concat(Array.from(selectedClassrooms)),
  );

  return (
    <CocoModal
      onClose={onClose}
      header={
        <Stack direction='row'>
          <Typography fontWeight={600} color='white'>
            <Trans>Select classes to import users</Trans>
          </Typography>
        </Stack>
      }
    >
      <Stack
        width='70vw'
        minWidth='350px'
        maxWidth='600px'
        padding={3}
        paddingTop={1}
        justifyContent='space-between'
      >
        <Stack>
          <Stack mb={2} direction='row' justifyContent='flex-end'>
            <Tooltip title={_(msg`Logout from your google classroom account`)}>
              <Button
                onClick={async () => {
                  const confirmed = await Swal.fire({
                    title: _(msg`Are you sure?`),
                    text: _(msg`You will be logged out of Google Classroom`),
                    icon: 'warning',
                    showCancelButton: true,
                  });

                  if (!confirmed.isConfirmed) {
                    return;
                  }

                  const res = await httpClient.delete(`/user/google-classroom`);

                  if (res.status === 200) {
                    setConnected(false);
                    setClassrooms([]);
                    setSelectedClassrooms(new Set());
                    setSelectedUsers([]);
                  }
                }}
                size='small'
                variant='text'
              >
                <Typography color='var(--color-primary)' variant='body2'>
                  Disconnect Account
                </Typography>
              </Button>
            </Tooltip>
          </Stack>
          <Stack>
            <ClassroomList
              classrooms={classrooms}
              selectedUsers={selectedUsers}
              selectedClassrooms={selectedClassrooms}
              setSelectedUsers={setSelectedUsers}
              toggleClassroomSelection={toggleClassroomSelection}
              selectedClassroom={classroom}
              setSelectedClassroom={setClassroom}
              setSelectedClassrooms={setSelectedClassrooms}
              uniqueClassroomsSelected={uniqueClassroomsSelected}
              communityId={communityId}
            />
          </Stack>
        </Stack>
        <Box mt={2}></Box>
        {classrooms?.length > 0 && (
          <>
            <Divider />
            <Box mt={2}></Box>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
            >
              <Typography variant='caption'>
                {uniqueClassroomsSelected.size > 0 ? (
                  <Trans>
                    Users from <strong>{uniqueClassroomsSelected.size}</strong>{' '}
                    class{uniqueClassroomsSelected.size > 1 ? 'es' : ''}{' '}
                    selected
                  </Trans>
                ) : (
                  <Trans>0 selected</Trans>
                )}
              </Typography>
              <Box></Box>
              <LoadingButton
                loading={inviting}
                variant='contained'
                disabled={
                  selectedUsers.length === 0 && selectedClassrooms.size === 0
                }
                sx={{}}
                onClick={async () => {
                  setInviting(true);

                  // Gather all different classroom IDs from members
                  const classroomIds = new Set<string>();
                  selectedUsers.forEach((u) => {
                    classroomIds.add(u.classroomId);
                  });

                  selectedClassrooms.forEach((c) => {
                    classroomIds.add(c);
                  });

                  const classroomMap = new Map<string, Classroom>(
                    classrooms.map((c) => [c.id, c]),
                  );

                  const classroomMeta = Object.fromEntries(
                    Array.from(classroomIds).map((id) => {
                      const c = classroomMap.get(id);
                      return [id, c!];
                    }),
                  );

                  const ids = await sendClassroomInvites(
                    Array.from(selectedClassrooms).map((id) => {
                      return { id };
                    }),
                    selectedUsers.map((u) => {
                      const c = classroomMap.get(u.classroomId);
                      return {
                        email: u.email,
                        classroomId: c?.id,
                        role: u.role,
                        type: u.type,
                      };
                    }),
                    classroomMeta,
                    communityId,
                  );

                  setInvitationcodeIDs(ids);

                  await loadCommunityDetails();
                  setInviting(false);
                  // onClose();
                  setShareComplete(true);
                }}
              >
                <Trans>Import Selected</Trans>
              </LoadingButton>
            </Stack>
          </>
        )}
      </Stack>
    </CocoModal>
  );
}
